import { valibotResolver } from '@hookform/resolvers/valibot';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import * as v from 'valibot';
import { useSignIn, useSocialSignIn } from '@/api/auth/queries';
import GithubIcon from '@/assets/socials/github.svg?react';
import GoogleIcon from '@/assets/socials/google.svg?react';
import { PasswordInput } from '@/components/password-input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { authClient } from '@/lib/auth-client';

const loginSchema = v.object({
  email: v.pipe(v.string(), v.email('Please enter a valid email address')),
  password: v.pipe(v.string(), v.minLength(1, 'Password is required')),
});

type LoginFormData = v.InferInput<typeof loginSchema>;

export function LoginForm() {
  const { redirect } = useSearch({ strict: false }) as { redirect: string | undefined };
  const signInMutation = useSignIn();
  const socialSignInMutation = useSocialSignIn();
  const navigate = useNavigate();
  const lastMethod = authClient.getLastUsedLoginMethod();

  const form = useForm<LoginFormData>({
    resolver: valibotResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    return signInMutation.mutateAsync(data, {
      onSuccess: () => {
        setTimeout(() => {
          navigate({ to: redirect ? redirect : '/projects' });
        }, 0);
      },
    });
  };

  const handleSocialSignIn = (provider: 'github' | 'google') => {
    const callback = `/oauth/success?redirect=${redirect || '/projects'}`;
    socialSignInMutation.mutate({ provider, callbackURL: callback });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h1>Login to your account</h1>
        </CardTitle>
        <CardDescription>Enter your email below to login to your account</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="m@example.com"
                      disabled={form.formState.isSubmitting}
                      autoComplete="email"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription hidden={true}>
                    Your email should be in the format e.g. user@example.com
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center">
                    <FormLabel>Password</FormLabel>
                    <Link
                      to="/forgot-password"
                      className="ml-auto inline-block text-sm underline-offset-4 hover:underline"
                    >
                      Forgot your password?
                    </Link>
                  </div>
                  <FormControl>
                    <PasswordInput
                      disabled={form.formState.isSubmitting}
                      autoComplete="current-password"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription hidden={true}>Enter your password</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="w-full relative"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? 'Signing in...' : 'Login'}
              {lastMethod === 'email' && (
                <Badge
                  variant="secondary"
                  className="ml-2 absolute border-1 border-muted-foreground -top-2.5 -right-2.5"
                >
                  Last used
                </Badge>
              )}
            </Button>
          </form>
        </Form>

        <div className="after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t py-6">
          <span className="bg-card text-muted-foreground relative z-10 px-2">Or continue with</span>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <Button
            className="relative"
            variant="outline"
            type="button"
            onClick={() => handleSocialSignIn('github')}
            disabled={form.formState.isSubmitting || socialSignInMutation.isPending}
          >
            <GithubIcon className="mr-2 h-4 w-4" />
            GitHub
            {lastMethod === 'github' && (
              <Badge
                variant="secondary"
                className="ml-2 absolute border-1 border-muted-foreground -top-2.5 -right-2.5"
              >
                Last used
              </Badge>
            )}
          </Button>
          <Button
            className="relative"
            variant="outline"
            type="button"
            onClick={() => handleSocialSignIn('google')}
            disabled={form.formState.isSubmitting || socialSignInMutation.isPending}
          >
            <GoogleIcon className="mr-2 h-4 w-4" />
            Google
            {lastMethod === 'google' && (
              <Badge
                variant="secondary"
                className="ml-2 absolute border-1 border-muted-foreground -top-2.5 -right-2.5"
              >
                Last used
              </Badge>
            )}
          </Button>
        </div>

        <div className="mt-4 text-center text-sm">
          Don&apos;t have an account?{' '}
          <Link to="/sign-up" search={{ redirect }} className="underline">
            Sign up
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
